import React, { memo, useEffect } from 'react'

import {
  Notice,
  Icon,
  RotatingSpinner,
  CircleX,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { iconSizeMedium } from 'Pods/ThreeDStudio/constants'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { dismissExportNotification } from 'Pods/ThreeDStudio/Store/actions/export'
import { EXPORT_STATUS, isExportOwnedByCurrentView } from 'Pods/ThreeDStudio/Store/slices/export'

const AUTO_HIDE_MS = 15000

const buildIcon = (svg, color) => (
  <Icon svgComponent={svg} color={color} notSpaced size={iconSizeMedium} />
)

const VARIANT_BY_STATUS = {
  [EXPORT_STATUS.QUEUED]: {
    type: 'info',
    icon: buildIcon(RotatingSpinner, 'primary'),
    title: 'The file has been added to the queue.',
    description: 'Your export will start shortly.',
    dismissible: false,
    autoHide: false,
  },
  [EXPORT_STATUS.PREPARING]: {
    type: 'info',
    icon: buildIcon(RotatingSpinner, 'primary'),
    title: 'Preparing your file for download.',
    description: null,
    dismissible: false,
    autoHide: false,
  },
  [EXPORT_STATUS.ERROR]: {
    type: 'danger',
    icon: buildIcon(CircleX, 'warning'),
    title: 'Something went wrong',
    description: "We couldn't complete the export. Please try again.",
    dismissible: true,
    autoHide: true,
  },
}

function ExportNotification() {
  const status = useThreeDStudioStore(state => state.export.status)
  const isOwnedByCurrentView = useThreeDStudioStore(isExportOwnedByCurrentView)
  const variant = VARIANT_BY_STATUS[status]

  const shouldRender = !!variant && isOwnedByCurrentView

  useEffect(() => {
    if (!shouldRender || !variant?.autoHide) return undefined
    const timer = setTimeout(dismissExportNotification, AUTO_HIDE_MS)
    return () => clearTimeout(timer)
  }, [shouldRender, status, variant])

  if (!shouldRender) return null

  return (
    <div
      className={classnames('export-notification', variant.description && 'export-notification--with-description')}
      role="status"
      aria-live="polite"
    >
      <Notice
        type={variant.type}
        useSSRIcon
        ssrIcon={variant.icon}
        onClose={variant.dismissible ? dismissExportNotification : null}
      >
        <div className="export-notification__body">
          <span className="cgt-heading-50">{variant.title}</span>
          {variant.description && <span className="cgt-body-base">{variant.description}</span>}
        </div>
      </Notice>
    </div>
  )
}

export default memo(ExportNotification)
